import React, { memo, useState, useEffect } from 'react'

import { Icon, Search as SearchIcon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, object, string } from 'prop-types'

import i18n from 'Pods/Shared/I18n'
import { useGroupedLiveSearch } from 'Pods/UnifiedNavigation/Hooks/useGroupedLiveSearch'
import { useSearch } from 'Pods/UnifiedNavigation/Hooks/useSearch'

import ImageSearch from './Components/ImageSearch/ImageSearch'
import SuggestionRow from './Components/SuggestionRow/SuggestionRow'

let HomeSearchDropdown

function Search({
  isInListingPage,
  searchInputValue = '',
  searchPlaceholder,
  locale = 'en',
  searchDropdown = null,
}) {
  const {
    query,
    wrapperRef,
    suggestions,
    showSuggestions,
    onInputChange,
    handleKeyDown,
    handleFocus,
    handleFocusLeave,
    isFocused,
    submitting,
    handleSubmit,
    cursorPosition,
    eventSource,
  } = useSearch(isInListingPage, searchInputValue, Boolean(searchDropdown))
  // Below 390px the design shows a short "Search" placeholder; CSS cannot swap
  // placeholder text, so track the breakpoint and switch it in place.
  const [isCompact, setIsCompact] = useState(false)
  const [isDropdownLoaded, setIsDropdownLoaded] = useState(false)
  const [isImageSearchActive, setIsImageSearchActive] = useState(false)
  const t = (key) => i18n.t(key, { ns: 'rails', lng: locale })

  // The image-upload popover shares this anchor; hide the dropdown while it is open and
  // restore it (input is still focused) once it closes - same trade-off as the homepage
  // search (NewHomepage/Components/Search/SearchContent).
  const showSearchDropdown = isFocused && Boolean(searchDropdown) && !isImageSearchActive

  const { results: groupedResults, isLoading: isSearchLoading } = useGroupedLiveSearch({
    query,
    enabled: showSearchDropdown,
  })

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 389px)')
    const handleChange = () => setIsCompact(mediaQuery.matches)

    handleChange()
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  useEffect(() => {
    let isMounted = true

    if (showSearchDropdown && !isDropdownLoaded) {
      if (HomeSearchDropdown) {
        // The module ref persists at module scope; on a remount it can already be loaded while
        // this instance's state reset to false - sync state instead of re-importing.
        setIsDropdownLoaded(true)
      } else {
        import('Pods/LazyLoaded/HomeSearchDropdown/HomeSearchDropdown')
          .then((module) => {
            if (isMounted) {
              HomeSearchDropdown = module.default
              setIsDropdownLoaded(true)
            }
          })
          // eslint-disable-next-line no-console
          .catch((error) => console.error('Failed to load HomeSearchDropdown:', error))
      }
    }

    return () => {
      isMounted = false
    }
  }, [showSearchDropdown, isDropdownLoaded])

  return (
    <div className="unified-nav__search" ref={wrapperRef} onBlur={handleFocusLeave}>
      <form
        className="unified-nav__search-form"
        id="nav-search-form"
        acceptCharset="UTF-8"
        action="/search"
        method="get"
        onSubmit={handleSubmit}
      >
        <div className="unified-nav__field">
          {/* No role="combobox"/aria-expanded here: the dropdown is a region of links and buttons,
              not a listbox, and arrow keys are left to the browser - so combobox semantics would
              promise controls that do not exist. aria-controls is global and valid on a textbox. */}
          <input
            type="text"
            name="keywords"
            className="unified-nav__field-input cgt-body-md"
            placeholder={isCompact ? t('navigation.aria.search') : searchPlaceholder}
            autoComplete="off"
            value={query}
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            aria-controls={showSearchDropdown ? 'home-search-dropdown' : undefined}
          />
          <ImageSearch locale={locale} onActiveChange={setIsImageSearchActive} />
          <button
            type="submit"
            className="unified-nav__field-submit"
            aria-label={t('navigation.aria.search')}
          >
            {submitting ? (
              <span className="unified-nav__field-spinner" />
            ) : (
              <Icon notSpaced svgComponent={SearchIcon} size="small" color="light" />
            )}
          </button>
        </div>
      </form>

      {/* Portal target for the listing "Filters" trigger (RemainingFilters),
          pushed in from ListingPage.jsx below md. Empty here by design. */}
      {isInListingPage && (
        <div className="unified-nav__filters-slot js-remaining-filters" />
      )}

      {showSearchDropdown && isDropdownLoaded && HomeSearchDropdown && (
        <HomeSearchDropdown
          columns={searchDropdown.columns}
          ariaLabel={searchDropdown.ariaLabel}
          groupedResults={groupedResults}
          isLoading={isSearchLoading}
          query={query}
          eventSource={eventSource}
        />
      )}

      {/* Legacy flat list only without a dropdown config - in dropdown mode its fetching
          and keyboard handling are disabled in useSearch, so it must never reappear (e.g.
          while the image-search popover temporarily hides the dropdown panel). */}
      {!searchDropdown && suggestions.length > 0 && showSuggestions && (
        <div className="unified-nav__suggestions">
          <ul className="unified-nav__suggestions-list">
            {suggestions.map((resultRow, i) => (
              <li key={resultRow[0]}>
                <SuggestionRow
                  query={query}
                  result={resultRow[0]}
                  thumbnailUrl={resultRow[1].thumbnailUrl}
                  eventSource={eventSource}
                  className={i === cursorPosition ? 'is-active' : ''}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

Search.propTypes = {
  isInListingPage: bool.isRequired,
  searchInputValue: string,
  searchPlaceholder: string.isRequired,
  locale: string,
  searchDropdown: object,
}

export default memo(Search)
